/**
 * Home health demo store — seed builder.
 *
 * `buildSeed` is the only way an initial state is produced (first load, version
 * bump, presenter reset). Fixtures are composed here: configuration, the roster
 * with its ownership and per-diem dates resolved against demo day, the patients,
 * and the staged cast.
 */
import { addDaysToKey, addWorkingDaysToKey, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { primaryTerritory } from "../engine/roster";
import { territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { DEFAULT_CONFIG } from "@caredna-marlin/utils/homeHealthDemo/fixtures/config";
import { ALL_PATIENT_FIXTURES } from "@caredna-marlin/utils/homeHealthDemo/fixtures/patients";
import { ROSTER_FIXTURE, type RosterFixtureEntry } from "@caredna-marlin/utils/homeHealthDemo/fixtures/roster";
import { buildVirtualCensus } from "../fixtures/virtualCensus";
import { CAST } from "@caredna-marlin/utils/homeHealthDemo/fixtures/cast";
import { seedCast, seedCensusEpisodes, type CastSeed } from "./seedCast";
import { scheduleFor } from "./scheduledDays";
import {
  DEMO_SCHEMA_VERSION,
  EMPTY_LOCAL_OVERLAY,
  EMPTY_SERVER_SNAPSHOT,
  type ConfigState,
  type DateKey,
  type DemoPatient,
  type DemoState,
  type IsoString,
  type RosterEntry,
  type ServerBindings,
} from "@caredna-marlin/utils/homeHealthDemo/state";

export interface SeedOptions {
  demoToday: DateKey;
  nowIso: IsoString;
  bindings?: Partial<ServerBindings>;
  /** Stage the patient cast (default true). Tests of the engines pass false for a blank slate. */
  includeCast?: boolean;
}

export const EMPTY_BINDINGS: ServerBindings = {
  providerIdsByHandle: {},
  visitTypeIdsByKind: {},
  patientIdsByRef: {},
  episodeIdsByRef: {},
  shiftCenterIdsByName: {},
  cancellationReasonIdsByName: {},
};

/** Small deterministic hash so the virtual census is stable for a given demo day. */
export function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Sunday that ends the orientation ramp, given "week N of M" relative to demo day.
 * Priya in "week 2 of 4" on a Wednesday ramps through the end of the third week
 * from now (the current week plus two more).
 */
export function rampEndsOn(demoToday: DateKey, rampWeek: number, rampWeeks: number): DateKey {
  const remainingWeeksIncludingCurrent = Math.max(rampWeeks - rampWeek + 1, 1);
  return addDaysToKey(weekStartKey(demoToday), remainingWeeksIncludingCurrent * 7 - 1);
}

export function rosterEntryFromFixture(entry: RosterFixtureEntry, config: ConfigState, demoToday: DateKey): RosterEntry {
  const { declaredDayOffsets, lastWorkedOffset, lastContactOffset, ...rest } = entry;
  return {
    ...rest,
    territory: primaryTerritory({ territories: entry.territories, homeZip: entry.homeZip, territory: "Unassigned" }, config.branch),
    rampEndsOn: entry.rampWeek && entry.rampWeeks ? rampEndsOn(demoToday, entry.rampWeek, entry.rampWeeks) : undefined,
    declaredDays: declaredDayOffsets?.map((d) => addWorkingDaysToKey(demoToday, d)),
    lastWorkedOn: lastWorkedOffset !== undefined ? addDaysToKey(demoToday, lastWorkedOffset) : undefined,
    lastContactOn: lastContactOffset !== undefined ? addDaysToKey(demoToday, lastContactOffset) : undefined,
  };
}

export function seedRoster(config: ConfigState, demoToday: DateKey): Record<string, RosterEntry> {
  return Object.fromEntries(ROSTER_FIXTURE.map((entry) => [entry.handle, rosterEntryFromFixture(entry, config, demoToday)]));
}

export function seedPatients(config: ConfigState): Record<string, DemoPatient> {
  return Object.fromEntries(
    ALL_PATIENT_FIXTURES.map((p) => [
      p.ref,
      {
        ref: p.ref,
        displayName: p.displayName,
        firstName: p.firstName,
        lastName: p.lastName,
        payer: p.payer,
        zip: p.zip,
        territory: territoryForZip(config.branch, p.zip),
        constraints: p.constraints,
      },
    ]),
  );
}

/**
 * The patients the seed generates beyond the fixtures — the background census
 * every clinician's week is full of — so the store knows them by name and binds
 * them like anyone else.
 */
function generatedCensus(config: ConfigState, demoToday: DateKey, known: Record<string, DemoPatient>): Record<string, DemoPatient> {
  const out: Record<string, DemoPatient> = {};
  for (const p of scheduleFor(demoToday).patients) {
    if (known[p.ref]) continue;
    out[p.ref] = { ref: p.ref, displayName: p.displayName, firstName: p.firstName, lastName: p.lastName, payer: p.payer, zip: p.zip, territory: territoryForZip(config.branch, p.zip), constraints: p.constraints };
  }
  return out;
}

function withCensus(cast: CastSeed, config: ConfigState, demoToday: DateKey): CastSeed {
  const census = seedCensusEpisodes(config, demoToday, new Set(CAST.map((m) => m.ref)));
  return { ...cast, episodes: { ...census.episodes, ...cast.episodes }, planVisits: { ...census.planVisits, ...cast.planVisits } };
}

const EMPTY_CAST: CastSeed = { referrals: {}, holds: {}, readiness: {}, episodes: {}, planVisits: {}, items: [], constraints: {}, payers: {} };

export function buildSeed(opts: SeedOptions): DemoState {
  const config = DEFAULT_CONFIG;
  const patients = seedPatients(config);
  if (opts.includeCast !== false) Object.assign(patients, generatedCensus(config, opts.demoToday, patients));
  for (const p of scheduleFor(opts.demoToday).patients) if (patients[p.ref]) patients[p.ref] = { ...patients[p.ref], mrn: p.mrn };
  const cast = opts.includeCast === false ? EMPTY_CAST : withCensus(seedCast(config, patients, opts.demoToday, opts.nowIso), config, opts.demoToday);
  for (const [ref, payer] of Object.entries(cast.payers)) if (patients[ref]) patients[ref] = { ...patients[ref], payer };
  for (const [ref, constraints] of Object.entries(cast.constraints)) if (patients[ref]) patients[ref] = { ...patients[ref], constraints };
  return {
    meta: {
      schemaVersion: DEMO_SCHEMA_VERSION,
      seedId: `${opts.demoToday}:${opts.nowIso}`,
      seededAt: opts.nowIso,
      demoToday: opts.demoToday,
      realDays: [],
      bindings: { ...EMPTY_BINDINGS, ...opts.bindings },
      bound: false,
      excludedVisitIds: [],
      createdVisitIds: [],
    },
    config,
    roster: seedRoster(config, opts.demoToday),
    patients,
    referrals: cast.referrals,
    holds: cast.holds,
    readiness: cast.readiness,
    episodes: cast.episodes,
    planVisits: cast.planVisits,
    annotations: {},
    confirmationRounds: {},
    virtualCensus: buildVirtualCensus(hashSeed(opts.demoToday)),
    sandbox: null,
    worklistItems: cast.items,
    blockAddresses: {},
    presenter: { fired: [], log: [] },
    server: EMPTY_SERVER_SNAPSHOT,
    local: EMPTY_LOCAL_OVERLAY,
  };
}
